import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {Subscription} from 'rxjs';

type DiscountType = 'amount' | 'rate' | '' | null;

type DiscountFormValue = {
  discountType: DiscountType;
  discountValue: number | string | null;
  discountDeadline: string | null;
};

@Component({
  selector: 'gatewee-discount',
  templateUrl: './gatewee-discount.component.html',
  styleUrls: ['./gatewee-discount.component.css'],
  standalone: false,
})
export class GateweeDiscountComponent implements OnInit, OnChanges, OnDestroy {
  formArray!: FormArray<FormGroup>;

  formSaved: Array<DiscountFormValue | null> = [];
  saved: boolean[] = [];

  discountTypes: Array<{label: string; value: 'rate' | 'amount'}>;
  discountDeadlines: Array<{label: string; value: string}> = [];

  @Input() formDiscount!: FormGroup;

  private formSub?: Subscription;
  private typeSub?: Subscription;

  constructor(private fb: FormBuilder, public translate: TranslateService) {
    this.discountDeadlines = [
      {
        label: this.translate.instant(
          'features.subscriptionsPlans.form.discountDeadlines.dueday_zero',
        ),
        value: '0',
      },
      {
        label: this.translate.instant(
          'features.subscriptionsPlans.form.discountDeadlines.dueday_one',
        ),
        value: '1',
      },
      {
        label: this.translate.instant(
          'features.subscriptionsPlans.form.discountDeadlines.dueday_two',
        ),
        value: '2',
      },
      {
        label: this.translate.instant(
          'features.subscriptionsPlans.form.discountDeadlines.dueday_three',
        ),
        value: '3',
      },
      {
        label: this.translate.instant(
          'features.subscriptionsPlans.form.discountDeadlines.dueday_four',
        ),
        value: '4',
      },
      {
        label: this.translate.instant(
          'features.subscriptionsPlans.form.discountDeadlines.dueday_five',
        ),
        value: '5',
      },
      {
        label: this.translate.instant(
          'features.subscriptionsPlans.form.discountDeadlines.dueday_six',
        ),
        value: '6',
      },
    ];

    this.discountTypes = [
      {
        label: this.translate.instant(
          'features.subscriptionsPlans.form.discountTypes.percentage',
        ),
        value: 'rate',
      },
      {
        label: this.translate.instant(
          'features.subscriptionsPlans.form.discountTypes.fixed',
        ),
        value: 'amount',
      },
    ];
  }

  ngOnInit(): void {
    this.formArray = this.fb.array<FormGroup>([]);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['formDiscount']?.currentValue) return;

    this.formSub?.unsubscribe();

    this.hydrateFromParent();

    this.formSub = this.formDiscount.valueChanges.subscribe(() => {
      if (this.formArray?.length === 0) this.hydrateFromParent();
      else if (this.saved[0]) this.formSaved[0] = this.getParentValue();
    });
  }

  ngOnDestroy(): void {
    this.formSub?.unsubscribe();
    this.typeSub?.unsubscribe();
  }

  private hasValue(v: unknown): boolean {
    return v !== null && v !== undefined && v !== '';
  }

  private hasDiscountData(v: DiscountFormValue): boolean {
    return (
      this.hasValue(v.discountType) ||
      this.hasValue(v.discountValue) ||
      this.hasValue(v.discountDeadline)
    );
  }

  private getParentValue(): DiscountFormValue {
    const v = this.formDiscount.getRawValue() as DiscountFormValue;

    return {
      discountType: (v.discountType ?? '') as DiscountType,
      discountValue: v.discountValue ?? '',
      discountDeadline: v.discountDeadline ?? '',
    };
  }

  private cloneFormGroup(template: FormGroup): FormGroup {
    const controls: Record<string, FormControl> = {};

    Object.keys(template.controls).forEach(key => {
      const c = template.controls[key] as FormControl;
      controls[key] = new FormControl(c.value, c.validator, c.asyncValidator);
      if (c.disabled) controls[key].disable({emitEvent: false});
    });

    return this.fb.group(controls);
  }

  private hydrateFromParent(): void {
    if (!this.formDiscount || !this.formArray) return;

    const value = this.getParentValue();
    if (!this.hasDiscountData(value)) return;

    if (this.formArray.length === 0) {
      const fg = this.cloneFormGroup(this.formDiscount);
      fg.patchValue(value, {emitEvent: false});
      this.formArray.push(fg);

      this.bindCardTypeChange(fg);

      this.formSaved = [value];
      this.saved = [true];
      return;
    }

    const fg = this.formArray.at(0);
    fg.patchValue(value, {emitEvent: false});
    this.bindCardTypeChange(fg);

    this.formSaved[0] = value;
    this.saved[0] = true;
  }

  private setCardRequiredValidators(cardForm: FormGroup): void {
    const typeCtrl = cardForm?.get('discountType');
    const valueCtrl = cardForm?.get('discountValue');
    const deadlineCtrl = cardForm?.get('discountDeadline');

    typeCtrl?.setValidators([Validators.required]);
    deadlineCtrl?.setValidators([Validators.required]);

    this.setDiscountValueValidators(cardForm);

    typeCtrl?.updateValueAndValidity({emitEvent: false});
    deadlineCtrl?.updateValueAndValidity({emitEvent: false});
    valueCtrl?.updateValueAndValidity({emitEvent: false});
  }

  private bindCardTypeChange(cardForm: FormGroup): void {
    this.typeSub?.unsubscribe();

    this.setDiscountValueValidators(cardForm);

    this.typeSub = cardForm
      ?.get('discountType')
      ?.valueChanges.subscribe(() => this.setDiscountValueValidators(cardForm));
  }

  private setDiscountValueValidators(cardForm: FormGroup): void {
    const type = cardForm?.get('discountType')?.value as DiscountType;
    const valueCtrl = cardForm?.get('discountValue');
    if (!valueCtrl) return;

    valueCtrl.clearValidators();

    const validators =
      type === 'rate'
        ? [Validators.required, this.maxPercentValidator(100)]
        : [Validators.required];

    valueCtrl.setValidators(validators);
    valueCtrl.updateValueAndValidity({emitEvent: false});
  }

  addDiscount(): void {
    if (this.formArray.length > 0) return;

    const newForm = this.cloneFormGroup(this.formDiscount);
    newForm.reset(
      {discountType: '', discountValue: '', discountDeadline: ''},
      {emitEvent: false},
    );

    this.setCardRequiredValidators(newForm);

    this.formArray.push(newForm);
    this.bindCardTypeChange(newForm);

    this.formSaved = [null];
    this.saved = [false];
  }

  removeSingle(): void {
    if (this.formArray?.length === 0) return;

    this.formArray.removeAt(0);
    this.formSaved = [];
    this.saved = [];

    this.typeSub?.unsubscribe();
    this.typeSub = undefined;

    this.formDiscount.reset(
      {discountType: '', discountValue: '', discountDeadline: ''},
      {emitEvent: true},
    );
    this.formDiscount.markAsPristine();
    this.formDiscount.updateValueAndValidity({emitEvent: true});
  }

  edit(i: number): void {
    if (!this.saved[i]) return;

    const savedValue = this.formSaved[i];
    if (!savedValue) return;

    this.saved[i] = false;
    this.formArray.at(i).patchValue(savedValue, {emitEvent: false});

    const fg = this.formArray.at(i);
    fg.patchValue(savedValue, {emitEvent: false});

    this.setCardRequiredValidators(fg);
  }

  saveSingle(i: number): void {
    const form = this.formArray.at(i);
    if (!form || form.invalid) return;

    const rawValue = form.getRawValue() as DiscountFormValue;

    this.formSaved[i] = rawValue;
    this.saved[i] = true;

    this.formDiscount.patchValue(rawValue, {emitEvent: true});
    this.formDiscount.markAsDirty();
    this.formDiscount.updateValueAndValidity({emitEvent: true});
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  removeDiscount(index: number): void {
    this.removeSingle();
  }

  showDeadline(value?: string | null): string {
    if (!value) return 'Não informado';
    const deadline = this.discountDeadlines.find(d => d.value === value);
    return deadline?.label ?? 'Não informado';
  }

  showValue(formValue: DiscountFormValue | null): string {
    if (!formValue) return 'Não informado';

    if (formValue.discountType === 'rate') {
      const n = Number(formValue.discountValue);
      return Number.isFinite(n) ? `${n}%` : 'Não informado';
    }

    const n = Number(formValue.discountValue);
    if (!Number.isFinite(n)) return 'Não informado';

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  }

  maxPercentValidator(max: number) {
    return (control: AbstractControl) => {
      const value = Number(control.value);
      if (Number.isNaN(value)) return null;
      return value > max ? {notMoreThan100: true} : null;
    };
  }
}
